import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function SignInModal({ isOpen, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('password');
  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData(event.target);
    const identifier = formData.get('identifier');
    const password = formData.get('password');

    try {
      const response = await api.loginWithPassword(identifier, password);
      const accessToken = response.data.access_token;
      
      login(accessToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      const profileResponse = await api.getUserProfile();
      const userProfile = profileResponse.data;
      
      console.log('User profile loaded:', userProfile);
      
      onClose();
      
      if (userProfile.role === 'admin') {
        toast.success('Welcome to admin panel!');
        navigate('/admin', { replace: true });
      } else {
        toast.success('Signed in successfully!');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login failed - Full error object:', error);
      console.error('Error type:', typeof error);
      console.error('Error keys:', Object.keys(error || {}));
      
      let errorMessage = 'Invalid credentials. Please try again.';
      
      if (error.response) {
        console.error('Backend error status:', error.response.status);
        console.error('Backend error data:', error.response.data);
        console.error('Backend error headers:', error.response.headers);
        
        errorMessage = error.response.data?.detail || 
                      error.response.data?.message || 
                      `Server error (${error.response.status})`;
      } else if (error.request) {
        console.error('No response from server');
        console.error('Request details:', {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        });
        errorMessage = 'Cannot reach server. Please check your connection.';
      } else {
        console.error('Request setup error:', error.message);
        errorMessage = 'Network error: ' + error.message;
      }
      
      console.error('Final error message to user:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthLinkRequest = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData(event.target);
    const identifier = formData.get('identifier');

    try {
      await api.requestAuthLink(identifier);
      setEmailSent(true);
      toast.success('Authentication link sent! Check your email or SMS.');
    } catch (error) {
      console.error('Auth link request failed:', error);
      toast.error('Failed to send authentication link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center">Welcome Back</DialogTitle>
          <DialogDescription className="text-center">
            Sign in to access your dashboard
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="password">Password</TabsTrigger>
            <TabsTrigger value="link">Magic Link</TabsTrigger>
          </TabsList>

          <TabsContent value="password" className="space-y-4 mt-4">
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="modal-identifier">Email or Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="modal-identifier"
                    name="identifier"
                    type="text"
                    placeholder="your@email.com or username"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="modal-password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="modal-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="text-center">
              <button
                onClick={() => {
                  onClose();
                  navigate('/auth');
                }}
                className="text-sm text-blue-600 hover:underline"
              >
                Forgot password?
              </button>
            </div>
          </TabsContent>

          <TabsContent value="link" className="space-y-4 mt-4">
            {emailSent ? (
              <div className="text-center py-8">
                <Mail className="h-16 w-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Check Your Messages</h3>
                <p className="text-gray-600">
                  We've sent you a sign-in link. Click it to access your account.
                </p>
              </div>
            ) : (
              <form onSubmit={handleAuthLinkRequest} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-link-identifier">Email or Phone</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input
                      id="modal-link-identifier"
                      name="identifier"
                      type="text"
                      placeholder="your@email.com or +1234567890"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Magic Link'}
                </Button>
              </form>
            )}
          </TabsContent>
        </Tabs>

        <div className="text-center text-sm text-gray-600">
          Don't have an account?{' '}
          <button
            onClick={() => {
              onClose();
              navigate('/auth');
            }}
            className="text-blue-600 hover:underline font-medium"
          >
            Sign up
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
